import { useEffect, useState } from "react";
import styles from "./lead.module.scss";
import { ReactComponent as HouseIcon } from "assets/house.svg";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import { ReactComponent as CrosshairIcon } from "assets/crosshair.svg";
import { ReactComponent as ExpandIcon } from "assets/expand.svg";
import { store } from "stores/Store";

import PractitionerInfo from "./PractitionerInfo";
import LeadForm from "./LeadForm";
import useExternalApiService from "helpers/ExternalApiService";
import { useNavigate } from "react-router-dom";
import { useDebouncedState } from "helpers/useDebouncedState";

interface Props {
  initialSearchValue: string;
  extraArgs?: string;
}

export interface Practs {
  name: string;
  firstname: string;
  lastname: string;
  companyname: string;
  address: string;
  state: string;
  city: string;
  zipcode: string;
  specialty: string;
  phone: string;
  grade: number;
  email: string;
  id?: string;
  ready_to_purchase?: boolean;
  latitude?: number;
  longitude?: number;
};

interface PlaceSuggestion {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

interface PlacesResponse {
  success: boolean;
  error_msg: string;
  places: PlaceSuggestion[];
}

const CreateLead: React.FC<Props> = ({ initialSearchValue, extraArgs }) => {
  const [ debouncedSearchStr, searchStr, setSearchStr ] = useDebouncedState(initialSearchValue);
  const [ selectedPractitioner, setSelectedPractitioner ] = useState<Partial<Practs> | null>(null);
  const [ viewForm, setViewForm ] = useState("");
  const [ currentLocation, setCurrentLocation ] = useState<any>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isPresetDropdownOpen, setIsPresetDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);
  
  const presetOptions = [
    { value: "acupuncture", label: "Acupuncture" },
    { value: "allergies", label: "Allergies" },
    { value: "allergy", label: "Allergy" },
    { value: "alternative medicine", label: "Alternative Medicine" },
    { value: "amin", label: "Amin" },
    { value: "arthritis", label: "Arthritis" },
    { value: "cardiologist", label: "Cardiologist" },
    { value: "cardiology", label: "Cardiology" },
    { value: "cardiovascular", label: "Cardiovascular" },
    { value: "chiropractic", label: "Chiropractic" },
    { value: "chiropractor", label: "Chiropractor" },
    { value: "chiropractors", label: "Chiropractors" },
    { value: "clinical", label: "Clinical" },
    { value: "cnc", label: "CNC" },
    { value: "coach", label: "Coach" },
    { value: "compounding pharmacy", label: "Compounding Pharmacy" },
    { value: "cranial sacral", label: "Cranial Sacral" },
    { value: "d.o.", label: "D.O." },
    { value: "dc", label: "DC" },
    { value: "dds", label: "DDS" },
    { value: "dentist", label: "Dentist" },
    { value: "dermatologist", label: "Dermatologist" },
    { value: "dietician", label: "Dietician" },
    { value: "dnm", label: "DNM" },
    { value: "dnp", label: "DNP" },
    { value: "do", label: "DO" },
    { value: "doctor", label: "Doctor" },
    { value: "doctors", label: "Doctors" },
    { value: "dpt", label: "DPT" },
    { value: "emg", label: "EMG" },
    { value: "family medicine", label: "Family Medicine" },
    { value: "fnp", label: "FNP" },
    { value: "functional medicine", label: "Functional Medicine" },
    { value: "gynecologist", label: "Gynecologist" },
    { value: "gynecology", label: "Gynecology" },
    { value: "healing", label: "Healing" },
    { value: "health", label: "Health" },
    { value: "health coach", label: "Health Coach" },
    { value: "health store", label: "Health Store" },
    { value: "holistic", label: "Holistic" },
    { value: "homeopathic", label: "Homeopathic" },
    { value: "homeopathy", label: "Homeopathy" },
    { value: "hrt", label: "HRT" },
    { value: "integrated", label: "Integrated" },
    { value: "integrative", label: "Integrative" },
    { value: "internal medicine", label: "Internal Medicine" },
    { value: "l.ac", label: "L.Ac" },
    { value: "lac", label: "Lac" },
    { value: "m.d.", label: "M.D." },
    { value: "md", label: "MD" },
    { value: "mdvip", label: "MDVIP" },
    { value: "med spa", label: "Med Spa" },
    { value: "medical spa", label: "Medical Spa" },
    { value: "mph", label: "MPH" },
    { value: "natural medicine", label: "Natural Medicine" },
    { value: "naturopath", label: "Naturopath" },
    { value: "naturopathic", label: "Naturopathic" },
    { value: "nd", label: "ND" },
    { value: "neuro", label: "Neuro" },
    { value: "nmd", label: "NMD" },
    { value: "nourishing", label: "Nourishing" },
    { value: "nutrition", label: "Nutrition" },
    { value: "nutritionist", label: "Nutritionist" },
    { value: "ob/gyn", label: "OB/GYN" },
    { value: "orthopedic", label: "Orthopedic" },
    { value: "pa", label: "PA" },
    { value: "pa-c", label: "PA-C" },
    { value: "pain management", label: "Pain Management" },
    { value: "pediatrician", label: "Pediatrician" },
    { value: "pediatrics", label: "Pediatrics" },
    { value: "pharmacist", label: "Pharmacist" },
    { value: "pharmacy", label: "Pharmacy" },
    { value: "phd", label: "PhD" },
    { value: "physicians", label: "Physicians" },
    { value: "plastic surgery", label: "Plastic Surgery" },
    { value: "post", label: "Post" },
    { value: "preventative medicine", label: "Preventative Medicine" },
    { value: "pt", label: "PT" },
    { value: "rd", label: "RD" },
    { value: "rdn", label: "RDN" },
    { value: "registered dietitian", label: "Registered Dietitian" },
    { value: "rn", label: "RN" },
    { value: "supplements", label: "Supplements" },
    { value: "surgeon", label: "Surgeon" },
    { value: "urgent care", label: "Urgent Care" },
    { value: "weight loss", label: "Weight Loss" },
    { value: "weightloss", label: "Weightloss" },
    { value: "wellness", label: "Wellness" },
  ];

  const handlePresetSelect = (value: string) => {
    setSearchStr(value);
    setIsPresetDropdownOpen(false);
  };

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCurrentLocation({
          description: "Current Location",
          place_id: "current_location",
          terms: [
            { value: "Current Location" },
            { value: `${lat.toFixed(6)}, ${lng.toFixed(6)}` }
          ],
          geometry: {
            location: {
              lat,
              lng
            }
          }
        });
      });
    }

    if (extraArgs === "edit") {
      setViewForm("form");
      setSelectedPractitioner({
        name: store.currentPractitioner?.name,
        address: store.currentPractitioner?.address,
        phone: store.currentPractitioner?.phone,
        email: store.currentPractitioner?.email,
        firstname: store.currentPractitioner?.firstname,
        lastname: store.currentPractitioner?.lastname,
        companyname: store.currentPractitioner?.companyname,
        city: store.currentPractitioner?.city,
        zipcode: store.currentPractitioner?.zipcode,
        state: store.currentPractitioner?.state,
        specialty: store.currentPractitioner?.specialty,      
        id: store.currentPractitioner?.id,
        ready_to_purchase: Boolean(store.currentPractitioner?.ready_to_purchase),
        latitude: store.currentPractitioner?.latitude,
        longitude: store.currentPractitioner?.longitude,
      });
    }
  }, []);

  const onSuggestionClick = async (suggestion: any) => {
    const result = await postData('/api/google_places_details', {
      place_id: suggestion.place_id,
    });
    //console.log(result);
    setSelectedPractitioner(result);

    //console.log(viewForm, selectedPractitioner);

  }


  const getPlacesAutocomplete = async (input: string) => {
    return new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const currentLocationString = `${lat},${lng}`;
          
          try {
            const result = await postData('/api/google_places_autocomplete', {
              query: input,
              location: currentLocationString,
            });
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, (error) => {
          reject(error);
        });
      } else {
        reject(new Error('Geolocation not supported'));
      }
    });
  };

  useEffect(() => {
    if (debouncedSearchStr) {
      getPlacesAutocomplete(debouncedSearchStr).then((response) => {
        const r = response as PlacesResponse;
        if (r.success) {
          setSuggestions(r.places);
        }
      }).catch((err) => console.error(err));
    }
  }, [debouncedSearchStr]);

  if (viewForm && selectedPractitioner) {
    return(
      <LeadForm
        practitionerInfo={viewForm === "form" ? selectedPractitioner : null}
        onNavigate={async (v) => {
          if (v === "lead") {
            setSelectedPractitioner(null);
            setViewForm("");
          }
          if (v === "draft") {
            alert("save draft")
            setSelectedPractitioner(null);
          }
          if (v === "submit") {
            alert("save lead")
            setSelectedPractitioner(null);
          }
          if (v === "backedit") {
            window.history.back();
          }
        }} />
    );
  };

  if (!viewForm && selectedPractitioner) {
    return (
      <PractitionerInfo 
        practitioner={selectedPractitioner}
        onNavigate={(v) => {
          if (v === "lead") {
            setSelectedPractitioner(null);
          }
          if (v === "form") {
            setViewForm(v);
          }
          if (v === "empty-form") {
            setViewForm(v)
          }
        }} />
    );
  };

  return (
    <div className={styles["create-lead-wrapper"]}>
      <div className={styles["search-bar"]}>
        <HouseIcon />
        <input
          autoFocus
          value={searchStr}
          onChange={(e) => setSearchStr(e.target.value)}
          type="text"
          className={styles["search-input"]}
        />
        <CloseIcon width={27} onClick={() => setSearchStr("")} />
      </div>
      <div className={styles["sub-search-bar-no-margin"]}>PICK THESE MATCHES FROM GOOGLE MAPS</div>
            
      <div className={styles["preset-section"]}>
        <div className={styles["preset-header"]} onClick={() => setIsPresetDropdownOpen(!isPresetDropdownOpen)}>
          <div className={styles["preset-header-text"]}>Quick Search Ideas</div>
          <ExpandIcon 
            className={`${styles["preset-expand-icon"]} ${isPresetDropdownOpen ? styles["expanded"] : ""}`}
            style={{width: '15px', height: '15px'}}
          />
        </div>
        {isPresetDropdownOpen && (
          <div className={styles["preset-options"]}>
            {presetOptions.map((option) => (
              <div
                key={option.value}
                className={styles["preset-option"]}
                onClick={() => handlePresetSelect(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <ul className={styles["practitioner-list"]}>
          {suggestions.length > 0 ? (
            suggestions.map((suggestion, index) => (
              <div
                key={index}
                className={styles.suggestionItem}
                onClick={() => onSuggestionClick(suggestion)}
              >
                <div className={styles.suggestionHeader}>{suggestion.terms[0].value}</div>
                <div className={styles.suggestionSubtext}>{suggestion.terms.slice(0, -1).map((term: any) => term.value).join(', ')}</div>
              </div>
            ))
          ) : (
            <div 
              //onClick={() => currentLocation && onSuggestionClick(currentLocation)} 
              className={styles.noSuggestions}
            >
              {/*<CrosshairIcon  style={{width: '25px', height: '25px', paddingLeft: '10px'}}/> Use current location */}
            </div>
          )}
      </ul>
      <div className={styles["button-group"]}>
        <p>Can't Find Practitioner?</p>
        <button
          onClick={() => {
            setViewForm("empty-form");
            setSelectedPractitioner({});
          }}
          className={styles["manual-button"]}
          type="button">
            ENTER MANUALLY
        </button>
      </div>
    </div>
  );
};

export default CreateLead;